import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import { Slot } from '@magicschool/spellbook/components/Slot';
import type { SpinnerProps } from '@magicschool/spellbook/components/Spinner';
import { Spinner } from '@magicschool/spellbook/components/Spinner';
import type { TextProps } from '@magicschool/spellbook/components/Text';
import { Text } from '@magicschool/spellbook/components/Text';
import type { ToggleButtonProps } from '@magicschool/spellbook/components/ToggleButton';
import { toggleButtonVariants } from '@magicschool/spellbook/components/ToggleButton';
import type { ToggleIconButtonProps } from '@magicschool/spellbook/components/ToggleIconButton';
import type { TooltipProps } from '@magicschool/spellbook/components/Tooltip';
import { Tooltip } from '@magicschool/spellbook/components/Tooltip';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getButtonTextVariant, getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as TogglePrimitive from '@radix-ui/react-toggle';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { memo } from 'react';

type ToggleGroupOrientation = 'horizontal' | 'vertical';

export const ToggleGroupItemSlot = Slot<
  React.ComponentProps<typeof ToggleGroupPrimitive.Item> & ToggleButtonProps & { orientation?: ToggleGroupOrientation }
>({
  displayName: 'ToggleGroup.Item',
  preventOverride: true,
  renderDefault: ({
    children,
    value,
    icon,
    isLoading,
    variant,
    size,
    fullWidth,
    justify,
    withBorder,
    orientation,
    shape,
    color,
    className,
    role,
    ...props
  }) => {
    const { slots } = useSlots(children, {
      button: {
        Component: ToggleGroupButtonSlot,
        props: { children, isLoading, variant, size, fullWidth, justify, icon, withBorder, orientation, shape, color, className },
      },
    });

    return (
      <ToggleGroupPrimitive.Item data-spellbook-name="toggle-group-item" value={value} role={role} {...props} asChild>
        {slots.button}
      </ToggleGroupPrimitive.Item>
    );
  },
});

type ToggleButtonSlottedContentProps = {
  slots: {
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    loadingIcon?: React.ReactNode;
  };
  rest?: React.ReactNode;
  isLoading?: boolean;
  size?: ToggleButtonProps['size'];
};

type ToggleIconButtonContentProps = Pick<ToggleIconButtonProps, 'name' | 'size' | 'label' | 'children' | 'isLoading'>;

const ToggleIconButtonContent = memo<ToggleIconButtonContentProps>(({ children, isLoading, name, label, size: providedSize }) => {
  const { slots, rest } = useSlots(children, {
    loadingIcon: {
      Component: ToggleGroupItemSpinnerSlot,
    },
  });

  if (isLoading) {
    return slots.loadingIcon;
  }

  const size = getControlIconSize(providedSize);

  return (
    <>
      <Icon name={name} size={size} />
      {(label || rest) && <span className="sr-only">{label || rest}</span>}
    </>
  );
});

const ToggleButtonSlottedContent = memo<ToggleButtonSlottedContentProps>(({ slots, rest, isLoading, size }) => {
  const textVariant = getButtonTextVariant(size);
  return (
    <>
      {isLoading ? slots.loadingIcon : slots.leadingIcon}
      <Text variant={textVariant} className="px-1" element="span">
        {rest}
      </Text>
      {slots.trailingIcon}
    </>
  );
});

const toggleGroupButtonVariants = cva('z-0 focus-visible:z-1 hover:z-1 aria-pressed:z-1 data-[state=on]:z-1', {
  variants: {
    fullWidth: {
      true: 'flex-1',
    },
    withBorder: {
      true: '',
    },
    orientation: {
      horizontal: '',
      vertical: '',
    },
  },
  compoundVariants: [
    {
      withBorder: true,
      orientation: 'horizontal',
      className: [
        'ltr:first:rounded-tr-none',
        'ltr:last-of-type:rounded-tl-none',
        'ltr:first:rounded-br-none',
        'ltr:last-of-type:rounded-bl-none',
        'rtl:first:rounded-tl-none',
        'rtl:last-of-type:rounded-tr-none',
        'rtl:first:rounded-bl-none',
        'rtl:last-of-type:rounded-br-none',
        'ltr:not-first:ml-[-1px]',
        'rtl:not-first:mr-[-1px]',
        'not-first:not-last-of-type:rounded-none',
      ],
    },
    {
      withBorder: true,
      orientation: 'vertical',
      className: ['first:rounded-b-none', 'last-of-type:rounded-t-none', 'not-first:mt-[-1px]', 'not-first:not-last-of-type:rounded-none'],
    },
  ],
  defaultVariants: { orientation: 'horizontal' },
});

const toggleGroupTooltipButtonVariants = cva('z-0 focus-visible:z-1 hover:z-1 has-[[aria-pressed=true]]:z-1 has-[[data-state=on]]:z-1', {
  variants: {
    fullWidth: {
      true: 'flex-1',
    },
    withBorder: {
      true: [
        'ltr:first-of-type:[&>*]:rounded-tr-none',
        'ltr:last-of-type:[&>*]:rounded-tl-none',
        'ltr:first-of-type:[&>*]:rounded-br-none',
        'ltr:last-of-type:[&>*]:rounded-bl-none',
        'rtl:first-of-type:[&>*]:rounded-tl-none',
        'rtl:last-of-type:[&>*]:rounded-tr-none',
        'rtl:first-of-type:[&>*]:rounded-bl-none',
        'rtl:last-of-type:[&>*]:rounded-br-none',
        'ltr:[&:not(:first-of-type)>*]:ml-[-1px]',
        'rtl:[&:not(:first-of-type)>*]:mr-[-1px]',
        '[&:not(:first-of-type):not(:last-of-type)>*]:rounded-none',
      ],
    },
  },
});

type BaseToggleGroupButtonProps = {
  isLoading?: boolean;
  variant?: VariantProps<typeof toggleButtonVariants>['variant'];
  size?: VariantProps<typeof toggleButtonVariants>['size'];
  shape?: VariantProps<typeof toggleButtonVariants>['shape'];
  withBorder?: boolean;
  orientation?: ToggleGroupOrientation;
  className?: string;
  pressed?: boolean;
  defaultPressed?: boolean;
  onPressedChange?: (pressed: boolean) => void;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  isInTooltip?: boolean;
};

type RegularToggleButtonProps = BaseToggleGroupButtonProps &
  Omit<ToggleButtonProps, keyof BaseToggleGroupButtonProps> & {
    icon: false;
    fullWidth?: boolean;
    justify?: VariantProps<typeof toggleButtonVariants>['justify'];
    name?: never;
    label?: never;
  };

type IconToggleButtonProps = BaseToggleGroupButtonProps &
  Omit<ToggleIconButtonProps, keyof BaseToggleGroupButtonProps> & {
    icon: true;
    fullWidth?: never;
    justify?: never;
    name: string;
    label?: string;
  };

type ToggleGroupButtonSlotProps = RegularToggleButtonProps | IconToggleButtonProps;

function isIconToggleButton(props: ToggleGroupButtonSlotProps): props is IconToggleButtonProps {
  return props.icon === true;
}

export const ToggleGroupButtonSlot = Slot<ToggleGroupButtonSlotProps>({
  displayName: 'ToggleGroup.Button',
  preventOverride: true,
  isRenderedByDefault: true,
  renderDefault: (props) => {
    const {
      children,
      value,
      icon,
      isLoading,
      variant,
      size,
      withBorder,
      orientation,
      shape,
      color,
      className,
      pressed,
      defaultPressed,
      onPressedChange,
      onClick,
      disabled,
      fullWidth: providedFullWidth,
      justify: providedJustify,
      isInTooltip,
      ...restProps
    } = props;

    const fullWidth = providedFullWidth;
    const justify = isIconToggleButton(props) ? undefined : providedJustify;

    const { slots, rest } = useSlots(children, {
      leadingIcon: {
        Component: ToggleGroupItemLeadingIconSlot,
        props: { size },
      },
      trailingIcon: { Component: ToggleGroupItemTrailingIconSlot, props: { size } },
      loadingIcon: { Component: ToggleGroupItemSpinnerSlot, props: { size } },
    });

    const isActuallyDisabled = disabled || isLoading;

    const handleOnPressedChange = (pressed: boolean) => {
      onPressedChange?.(pressed);
    };

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isActuallyDisabled) {
        event.preventDefault();
        event.stopPropagation();

        return;
      }

      onClick?.(event);
    };

    return (
      <TogglePrimitive.Root
        data-spellbook-name="toggle-group-button"
        className={cn(
          toggleButtonVariants({ variant, size, fullWidth, shape, justify, withBorder, icon, color }),
          toggleGroupButtonVariants({ fullWidth, withBorder: !isInTooltip && withBorder, orientation }),
          className,
        )}
        defaultPressed={defaultPressed}
        pressed={pressed}
        onPressedChange={handleOnPressedChange}
        onClick={handleOnClick}
        aria-disabled={isActuallyDisabled}
        aria-busy={isLoading}
        {...restProps}
      >
        {icon ? (
          <ToggleIconButtonContent name={props.name} size={size} label={props.label} isLoading={isLoading}>
            {rest}
          </ToggleIconButtonContent>
        ) : (
          <ToggleButtonSlottedContent slots={slots} rest={rest} isLoading={isLoading} size={size} />
        )}
      </TogglePrimitive.Root>
    );
  },
});

export const ToggleGroupItemLeadingIconSlot = Slot<IconProps>({
  displayName: 'ToggleGroup.ItemLeadingIcon',
  renderDefault: ({ name = 'square-dashed', size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="toggle-group-item-leading-icon" name={name} size={size} {...props} />;
  },
});

export const ToggleGroupItemTrailingIconSlot = Slot<IconProps>({
  displayName: 'ToggleGroup.ItemTrailingIcon',
  renderDefault: ({ name = 'square-dashed', size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Icon data-spellbook-name="toggle-group-item-trailing-icon" name={name} size={size} {...props} />;
  },
});

export const ToggleGroupItemSpinnerSlot = Slot<SpinnerProps>({
  displayName: 'ToggleGroup.ItemSpinner',
  isRenderedByDefault: true,
  renderDefault: ({ size: providedSize, ...props }) => {
    const size = getControlIconSize(providedSize);
    return <Spinner data-spellbook-name="toggle-group-item-spinner" size={size} {...props} />;
  },
});

export const ToggleGroupTooltipSlot = Slot<
  Omit<TooltipProps, 'asToggleTip'> & React.ComponentProps<typeof ToggleGroupPrimitive.Item> & ToggleButtonProps
>({
  displayName: 'ToggleGroup.Tooltip',
  spellbookName: false,
  preventOverride: true,
  renderDefault: (props) => {
    const {
      children,
      side = 'top',
      align = 'center',
      size,
      fullWidth,
      justify,
      variant,
      isLoading,
      disabled,
      value,
      icon,
      withBorder,
      shape,
      color,
      className,
      ...restProps
    } = props;

    const id = useId();

    const { slots } = useSlots(children, {
      tooltipContent: { Component: ToggleGroupTooltipContentSlot },
      trigger: {
        Component: ToggleGroupItemSlot,
        props: {
          size,
          value,
          icon,
          isLoading,
          variant,
          fullWidth,
          justify,
          withBorder,
          shape,
          color,
          'aria-describedby': id,
          isInTooltip: true,
        },
      },
    });

    return (
      <Tooltip className={cn('group', className)} side={side} align={align} asToggleTip={false} {...restProps}>
        <Tooltip.Trigger>
          <span className={cn(toggleGroupTooltipButtonVariants({ fullWidth, withBorder }))}>{slots.trigger}</span>
        </Tooltip.Trigger>
        {slots.tooltipContent && <Tooltip.Content id={id}>{slots.tooltipContent}</Tooltip.Content>}
      </Tooltip>
    );
  },
});

export const ToggleGroupTooltipContentSlot = Slot<TextProps>({
  displayName: 'ToggleGroup.TooltipContent',
  preventOverride: true,
  renderDefault: ({ ...props }) => (
    <Text data-spellbook-name="toggle-group-tooltip-content" variant="caption-1" element="span" {...props} />
  ),
});
